import { useState, memo, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, List, ListItem, Collapse, ListItemText, ListItemIcon, ListSubheader } from '@material-ui/core';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(5),
    color: theme.palette.text.primary
  })
);

const ListItemStyle = styled((props) => <ListItem button disableGutters {...props} />)(({ theme, isCollapsed }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: isCollapsed ? theme.spacing(2) : theme.spacing(5),
  paddingRight: isCollapsed ? theme.spacing(1) : theme.spacing(2.5),
  color: theme.palette.text.secondary,
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  minWidth: isCollapsed ? 48 : 'auto',
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  marginBottom: isCollapsed ? theme.spacing(0.5) : 0,
  borderRadius: isCollapsed ? theme.spacing(1) : 0,
  overflow: 'hidden',
  isolation: 'isolate',
  '&:hover': {
    backgroundColor: isCollapsed ? 'action.hover' : 'action.hover',
    borderRadius: isCollapsed ? theme.spacing(1) : 0,
    transform: isCollapsed ? 'scale(1.02)' : 'none',
    transition: 'all 0.2s ease-in-out'
  },
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

const NavItem = memo(({ item, active, isCollapsed, open, onToggle }) => {
  const theme = useTheme();
  const isActiveRoot = active(item.path, item.exact);
  const { title, path, icon, info, children } = item;
  const isChildActive = children?.some((child) => active(child.path));

  const handleOpen = useCallback(() => {
    onToggle?.();
  }, [onToggle]);

  const activeRootStyle = useMemo(() => ({
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  }), [theme.palette.primary.main, theme.palette.action.selectedOpacity]);

  const activeSubStyle = useMemo(() => ({
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  }), []);

  if (children) {
    const isParentActive = isActiveRoot || isChildActive;
    const listItem = (
      <ListItemStyle
        onClick={handleOpen}
        isCollapsed={isCollapsed}
        sx={{
          ...(isParentActive && activeRootStyle),
          cursor: 'pointer'
        }}
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        {!isCollapsed && (
          <>
            <ListItemText disableTypography primary={title} />
            {info && info}
            <Box
              component={Icon}
              icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{ width: 16, height: 16, ml: 1 }}
            />
          </>
        )}
      </ListItemStyle>
    );

    return (
      <>
        {isCollapsed ? (
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {listItem}
          </Box>
        ) : (
          listItem
        )}

        {!isCollapsed && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children?.map((item) => {
                const { title, path } = item;
                const isActiveSub = active(path);

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    isCollapsed={isCollapsed}
                    sx={{
                      ...(isActiveSub && activeSubStyle)
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && {
                            transform: 'scale(2)',
                            bgcolor: 'primary.main'
                          })
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  const listItem = (
    <ListItemStyle
      component={RouterLink}
      to={path}
      isCollapsed={isCollapsed}
      sx={{
        ...(isActiveRoot && activeRootStyle),
        cursor: 'pointer'
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {!isCollapsed && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );

  return isCollapsed ? (
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {listItem}
    </Box>
  ) : (
    listItem
  );
});

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  isCollapsed: PropTypes.bool,
  open: PropTypes.bool,
  onToggle: PropTypes.func
};

const NavSection = memo(({ navConfig, isCollapsed, ...other }) => {
  const { pathname, search } = useLocation();
  
  const match = useCallback(
    (path, exact = false) => {
      if (!path) return false;
      const [pathPart, queryPart] = path.split('?');
      const isPathMatch = !!matchPath({ path: pathPart, end: exact }, pathname);
      if (!isPathMatch) return false;
      if (queryPart) {
        return search.includes(queryPart);
      }
      if (search && pathname.startsWith('/dashboard/hawkowl')) {
        return false;
      }
      return true;
    },
    [pathname, search]
  );

  const activeParentTitle = useMemo(() => {
    for (const list of navConfig || []) {
      for (const item of list.items || []) {
        if (!item.children) continue;
        const isActiveRoot = match(item.path, item.exact);
        const isChildActive = item.children.some((child) => match(child.path));
        if (isActiveRoot || isChildActive) {
          return item.title;
        }
      }
    }
    return null;
  }, [navConfig, match]);

  const [openItemTitle, setOpenItemTitle] = useState(activeParentTitle);

  useEffect(() => {
    setOpenItemTitle(activeParentTitle);
  }, [activeParentTitle]);

  const handleToggle = useCallback((title) => {
    setOpenItemTitle((prev) => (prev === title ? null : title));
  }, []);

  const renderedItems = useMemo(() => {
    return navConfig?.map((list) => {
      const { subheader, items } = list;
      return (
        <List key={subheader} disablePadding>
          {/* <ListSubheaderStyle>{subheader}</ListSubheaderStyle> */}
          {items?.map((item) => (
            <NavItem
              key={item.title}
              item={item}
              active={match}
              isCollapsed={isCollapsed}
              open={item.children ? openItemTitle === item.title : false}
              onToggle={item.children ? () => handleToggle(item.title) : undefined}
            />
          ))}
        </List>
      );
    });
  }, [navConfig, match, isCollapsed, openItemTitle, handleToggle]);

  return (
    <Box {...other}>
      {renderedItems}
    </Box>
  );
});

NavSection.propTypes = {
  navConfig: PropTypes.array,
  isCollapsed: PropTypes.bool
};

NavSection.displayName = 'NavSection';

export default NavSection;
